/**
 * /producao/marcenaria — Planejamento Marcenaria (Sprint P5).
 *
 * Aloca ETAPAS INDIVIDUAIS de F6 (caixaria…carregamento) a executores de
 * produção — nunca "a fase inteira". Board kanban LATERAL (colunas = pessoas,
 * lado a lado com scroll horizontal do container — NUNCA empilhado):
 *
 *   Zona 1 — EntityHeader + toolbar (seletor de período Dia⇄Semana sempre
 *            com a data visível, filtro de etapa aplicado a TODOS os cards,
 *            ✨ Sugestões com badge, + Alocar etapa).
 *   Zona 2 — KpiStrip (capacidade alocada, sobrecarregados, sem responsável
 *            [clicável → scroll], SLA em risco).
 *   Zona 3 — pseudo-coluna "Sem responsável" no INÍCIO + 1 coluna por
 *            executor + trilho "Aguardando OSE" no FIM (fora das pessoas).
 *   Bloco B — tabela Capacidade/Interferências (linha → scroll+highlight
 *            da coluna, NÃO navega).
 *
 * Fonte: GET /api/producao/marcenaria/board?inicio&fim (ppcp:view). Ações:
 * alocar/desalocar (marcenaria:alocar — 422 de interferência vira aviso
 * inline + "Alocar mesmo assim") e indisponibilidade (operador:manage).
 * Sugestões são PLACEHOLDER rotulado (motor de regras a definir) — geradas
 * client-side dos dados reais, nunca "IA".
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiFileText, FiRepeat } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EmptyState from "../../components/EmptyState";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import UserAvatar from "../../components/UserAvatar";
import { useAuth } from "../../context/AuthContext";
import {
  BUFFER_ZONA_TONES,
  ETAPAS_F6,
  MOTIVOS_INDISPONIBILIDADE,
  NIVEL_OPERADOR_TONES,
  alocarEtapa,
  boardMarcenaria,
  criarIndisponibilidadeOperador,
  desalocarEtapa,
  listarIndisponibilidades,
  removerIndisponibilidadeOperador,
} from "../../services/producaoV2";
import {
  formatarDataCurta,
  formatarDataHoraCurta,
  formatarMinutosAtraso,
} from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Select,
  WarningMsg,
} from "../Orcamentos/styles";
import {
  BarraFerramentas,
  LinhaErro,
  Mono,
  NotaDiscreta,
  Shimmer,
  TabelaFixa,
  fmtNum,
} from "./_shared/ui";

const CHAVE_PERIODO = "prod.marcenaria.periodo";
const DIAS_CURTOS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const ETAPA_PINTURA = "PINTURA";
const HIGHLIGHT_MS = 1400;

// ---------------------------------------------------------------------------
// Datas do período (Dia ⇄ Semana) — sempre com a data visível
// ---------------------------------------------------------------------------

function dois(n) {
  return String(n).padStart(2, "0");
}

function dataISO(d) {
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}

function parseISO(iso) {
  const [ano, mes, dia] = String(iso).split("-").map(Number);
  return new Date(ano, mes - 1, dia);
}

function lerPeriodoInicial() {
  const hoje = dataISO(new Date());
  try {
    const raw = JSON.parse(window.localStorage.getItem(CHAVE_PERIODO) || "null");
    if (raw && (raw.modo === "dia" || raw.modo === "semana")
        && /^\d{4}-\d{2}-\d{2}$/.test(raw.ancora || "")) {
      return raw;
    }
  } catch {
    /* valor corrompido — cai no default */
  }
  return { modo: "dia", ancora: hoje };
}

/** Intervalo inclusivo [inicio, fim] do período selecionado. */
function intervaloDoPeriodo({ modo, ancora }) {
  if (modo === "dia") return { inicio: ancora, fim: ancora };
  const d = parseISO(ancora);
  const segunda = new Date(d);
  segunda.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const domingo = new Date(segunda);
  domingo.setDate(segunda.getDate() + 6);
  return { inicio: dataISO(segunda), fim: dataISO(domingo) };
}

/** "qua 15/jul" (dia) · "14–20/jul" ou "28/jul–03/ago" (semana). */
function rotuloPeriodo(periodo, intervalo) {
  if (periodo.modo === "dia") {
    const d = parseISO(periodo.ancora);
    return `${DIAS_CURTOS[d.getDay()]} ${formatarDataCurta(periodo.ancora)}`;
  }
  const ini = parseISO(intervalo.inicio);
  const fim = parseISO(intervalo.fim);
  if (ini.getMonth() === fim.getMonth()) {
    return `${dois(ini.getDate())}–${formatarDataCurta(intervalo.fim)}`;
  }
  return `${formatarDataCurta(intervalo.inicio)}–${formatarDataCurta(intervalo.fim)}`;
}

/** "07:39" a partir de horas decimais (7.65) — padrão hh:mm do brief. */
function fmtHoras(horas) {
  const total = Math.round((Number(horas) || 0) * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m > 0 ? `${h}h${dois(m)}` : `${h}h`;
}

function horaCurta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${dois(d.getHours())}:${dois(d.getMinutes())}`;
}

/** Faixa de utilização (cor só comunica estado): <85 ok · 85–100 atenção · >100 sobrecarga. */
function faixaUtilizacao(pct) {
  if (pct === null || pct === undefined) return "neutral";
  if (pct > 100) return "danger";
  if (pct >= 85) return "warning";
  return "success";
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const ToolbarEsquerda = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-right: auto;
`;

const GrupoModo = styled.div`
  display: inline-flex;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-md, 8px);
  padding: 2px;
  flex-shrink: 0;
`;

const ChipModo = styled.button`
  border: none;
  background: ${({ $ativo }) => ($ativo ? "var(--surface)" : "transparent")};
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-3)")};
  box-shadow: ${({ $ativo }) => ($ativo ? "var(--shadow-1)" : "none")};
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const InputData = styled.input`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 12px;
  font-family: var(--font-mono);
`;

const RotuloPeriodoTag = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-2);
  background: var(--accent-soft);
  border-radius: var(--radius-pill, 999px);
  padding: 4px 12px;
  white-space: nowrap;
`;

const Dropdown = styled.div`
  position: relative;
  display: inline-flex;
`;

const DropdownLista = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 60;
  min-width: 220px;
  padding: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md, 8px);
  box-shadow: var(--shadow-2);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OpcaoCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }
`;

const BadgeContagem = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--neutral-bg);
  border: 1px solid var(--neutral-border);
  color: var(--neutral-text);
  font-family: var(--font-mono);
  font-size: 11px;
`;

// ---------- Board (lanes LADO A LADO — scroll horizontal do container) ----

const BoardScroll = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  align-items: stretch;
  min-height: 320px;
`;

const ColunaBoard = styled.section`
  flex: 0 0 292px;
  width: 292px;
  max-height: 62vh;
  display: flex;
  flex-direction: column;
  background: var(--surface-2);
  border-radius: 16px;
  padding: 10px;
  transition: box-shadow 0.2s ease, opacity 0.2s ease;

  ${({ $esmaecida }) =>
    $esmaecida
    && css`
      opacity: 0.55;
      filter: grayscale(0.5);
    `}

  ${({ $destacada }) =>
    $destacada
    && css`
      box-shadow: 0 0 0 2px var(--accent);
    `}
`;

const HeaderColuna = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 4px 10px;
`;

const LinhaNome = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .nome {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
  }
`;

const LinhaCargo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--ink-3);
  flex-wrap: wrap;
`;

const LinhaCapacidade = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-2);
  white-space: nowrap;
`;

const MiniBarra = styled.div`
  flex: 1 1 auto;
  height: 6px;
  border-radius: 999px;
  background: var(--surface);
  overflow: hidden;

  > span {
    display: block;
    height: 100%;
    border-radius: 999px;
    width: ${({ $pct }) => Math.min(Math.max($pct || 0, 0), 100)}%;
    background: ${({ $faixa }) =>
      ($faixa === "danger" && "var(--danger-solid)")
      || ($faixa === "warning" && "var(--warning-solid)")
      || ($faixa === "neutral" && "var(--neutral-solid)")
      || "var(--success-solid)"};
  }
`;

const ToggleAtivo = styled.div`
  display: inline-flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px;
  align-self: flex-start;

  button {
    border: none;
    background: transparent;
    color: var(--ink-3);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 10px;
    border-radius: 999px;
    cursor: pointer;
  }
  button[data-ativo="true"] {
    background: var(--accent-soft);
    color: var(--accent);
    cursor: default;
  }
`;

const ListaCards = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px;
`;

const ColunaVazia = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--ink-4);
  font-size: 12px;
  padding: 12px;
`;

// ---------- Card compacto (anatomia das faixas do brief) ----------

const CardEtapaBox = styled.div`
  background: var(--surface);
  border: 1px solid
    ${({ $excecao }) => ($excecao ? "var(--danger-border)" : "var(--border)")};
  border-left: 3px solid
    ${({ $excecao }) => ($excecao ? "var(--danger-solid)" : "var(--border)")};
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:hover {
    border-color: ${({ $excecao }) =>
      $excecao ? "var(--danger-solid)" : "var(--border-strong)"};
    background: var(--surface-hover);
  }
`;

const FaixaContexto = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-3);

  .ctx {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: var(--font-mono);
  }
  .acoes {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
`;

const BotaoReatribuir = styled.button`
  border: none;
  background: none;
  color: var(--ink-3);
  padding: 2px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;

  &:hover {
    background: var(--accent-soft);
    color: var(--accent);
  }
`;

const TituloCard = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LinhaPills = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const RodapeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-3);

  .item {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    white-space: nowrap;
  }
  .atraso {
    color: var(--danger-text);
  }
  .janela {
    margin-left: auto;
    color: var(--ink-2);
  }
`;

// ---------- Trilho OSE ----------

const TrilhoOse = styled(ColunaBoard)`
  background: var(--surface);
  border: 1px dashed var(--border-strong);
`;

const CardOse = styled.div`
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;

  &:hover {
    background: var(--surface-hover);
  }

  .linha {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 11px;
    color: var(--ink-3);
    font-family: var(--font-mono);
    white-space: nowrap;
  }
`;

// ---------- Form (slide-overs) ----------

const CampoForm = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const RotuloCampo = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

const CampoInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const AreaTexto = styled.textarea`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const GradeJanela = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
`;

const RodapePainel = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
`;

const SugestaoBox = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--ink-2);

  .acoes {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
`;

const BotaoGhost = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-soft);
  }
`;

const LinhaTabelaEsmaecida = styled.tr`
  opacity: ${({ $esmaecida }) => ($esmaecida ? 0.5 : 1)};
`;

// ---------------------------------------------------------------------------
// Card de etapa (compartilhado entre colunas / sem responsável)
// ---------------------------------------------------------------------------

function CardEtapa({ card, modoSemana, onCorpo, onReatribuir }) {
  const excecao = card.interferencia || card.sla_risco;
  const janelaIni = horaCurta(card.janela_inicio);
  const janelaFim = horaCurta(card.janela_fim);
  const janela = janelaIni && janelaFim ? `${janelaIni}–${janelaFim}` : null;
  const prefixoDia = modoSemana && card.janela_inicio
    ? `${formatarDataCurta(card.janela_inicio)} · `
    : "";

  return (
    <CardEtapaBox
      $excecao={excecao}
      onClick={onCorpo}
      title={`${card.ambiente.numero} · ${card.ambiente.nome} — ${card.etapa_label}`}
    >
      <FaixaContexto>
        <span
          className="ctx"
          title={`${card.pedido_numero} · ${card.ambiente.numero} · ${card.cliente_nome || ""}`}
        >
          {card.pedido_numero} · {card.ambiente.numero}
          {card.cliente_nome ? ` · ${card.cliente_nome}` : ""}
        </span>
        <span className="acoes">
          {card.congelado && (
            <Pill $tone="warning" title="Ambiente congelado">
              ❄
            </Pill>
          )}
          {onReatribuir && (
            <BotaoReatribuir
              type="button"
              title="Reatribuir etapa (sem sair da tela)"
              aria-label="Reatribuir etapa"
              onClick={(e) => {
                e.stopPropagation();
                onReatribuir();
              }}
            >
              <FiRepeat size={13} aria-hidden="true" />
            </BotaoReatribuir>
          )}
        </span>
      </FaixaContexto>

      <TituloCard>
        {card.ambiente.nome} — {card.etapa_label}
      </TituloCard>

      <LinhaPills>
        <Pill $tone="neutral">{card.etapa_label}</Pill>
        <Pill $tone="purple">F6 Marcenaria</Pill>
        {card.lote_numero && <Pill $tone="neutral">{card.lote_numero}</Pill>}
        {card.interferencia && <Pill $tone="danger">Interferência</Pill>}
        {card.sla_risco && <Pill $tone="danger">SLA</Pill>}
        {card.etapa_codigo === ETAPA_PINTURA && (
          <Pill $tone="warning">Terceirizável</Pill>
        )}
      </LinhaPills>

      <RodapeCard>
        <span className="item" title="Peças do ambiente">
          <FiFileText size={11} aria-hidden="true" />
          {card.pecas_count}
        </span>
        <span
          className={`item${card.atraso_min > 0 ? " atraso" : ""}`}
          title="Atraso acumulado do ambiente"
        >
          ⏱ {formatarMinutosAtraso(card.atraso_min)}
        </span>
        <Pill
          $tone={BUFFER_ZONA_TONES[card.buffer?.zona] || "success"}
          title={`Consumo de buffer: ${card.buffer?.pct ?? 0}%`}
        >
          {card.buffer?.pct ?? 0}%
        </Pill>
        {janela && (
          <span
            className="janela"
            title={`Janela: ${formatarDataHoraCurta(card.janela_inicio)} – ${formatarDataHoraCurta(card.janela_fim)}`}
          >
            {prefixoDia}
            {janela}
          </span>
        )}
      </RodapeCard>
    </CardEtapaBox>
  );
}

// ---------------------------------------------------------------------------
// Slide-over Alocar / Reatribuir (Bloco C)
// ---------------------------------------------------------------------------

function AlocarPanel({
  aberto,
  contexto, // { modo: "criar"|"alocar"|"reatribuir", card?, deColaborador?, paraOperadorId? }
  colaboradores,
  semResponsavel,
  intervalo,
  onFechar,
  onSucesso,
}) {
  const { request } = useAuth();
  const navigate = useNavigate();

  const [etapaId, setEtapaId] = useState("");
  const [operadorId, setOperadorId] = useState("");
  const [data, setData] = useState("");
  const [horaInicio, setHoraInicio] = useState("08:00");
  const [horaFim, setHoraFim] = useState("10:00");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState(null);
  const [conflito, setConflito] = useState(null);

  const card = contexto?.card || null;
  const modo = contexto?.modo || "criar";

  useEffect(() => {
    if (!aberto) return;
    const hoje = dataISO(new Date());
    const dataDefault =
      hoje >= intervalo.inicio && hoje <= intervalo.fim
        ? hoje
        : intervalo.inicio;
    setEtapaId(card?.etapa_id || "");
    setOperadorId(contexto?.paraOperadorId || "");
    if (card?.janela_inicio) {
      const d = new Date(card.janela_inicio);
      setData(Number.isNaN(d.getTime()) ? dataDefault : dataISO(d));
      setHoraInicio(horaCurta(card.janela_inicio) || "08:00");
      setHoraFim(horaCurta(card.janela_fim) || "10:00");
    } else {
      setData(dataDefault);
      setHoraInicio("08:00");
      setHoraFim("10:00");
    }
    setObservacao("");
    setErro(null);
    setConflito(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const cardSelecionado = useMemo(() => {
    if (card) return card;
    return semResponsavel.find((c) => c.etapa_id === etapaId) || null;
  }, [card, etapaId, semResponsavel]);

  const operadorSelecionado = useMemo(
    () => colaboradores.find((c) => c.id === operadorId) || null,
    [colaboradores, operadorId],
  );

  const semCompetencia = Boolean(
    operadorSelecionado
    && cardSelecionado
    && (operadorSelecionado.competencias || []).length > 0
    && !operadorSelecionado.competencias.includes(cardSelecionado.etapa_codigo),
  );

  const sobrecarregado = Boolean(
    operadorSelecionado
    && operadorSelecionado.capacidade?.pct !== null
    && operadorSelecionado.capacidade?.pct > 100,
  );

  const salvar = async (forcar) => {
    setErro(null);
    if (!forcar) setConflito(null);
    const alvoEtapa = card?.etapa_id || etapaId;
    if (!alvoEtapa) {
      setErro("Selecione a etapa a alocar.");
      throw new Error("validação");
    }
    if (!operadorId) {
      setErro("Selecione o colaborador.");
      throw new Error("validação");
    }
    if (!data || !horaInicio || !horaFim) {
      setErro("Informe a data e a janela de horário (início e fim).");
      throw new Error("validação");
    }
    if (horaFim <= horaInicio) {
      setErro("O fim da janela deve ser depois do início.");
      throw new Error("validação");
    }
    try {
      await alocarEtapa(request, alvoEtapa, {
        operadorId,
        janelaInicio: `${data}T${horaInicio}:00`,
        janelaFim: `${data}T${horaFim}:00`,
        observacao: observacao.trim(),
        forcar,
      });
      onSucesso();
      onFechar();
    } catch (err) {
      if (err?.status === 422) {
        setConflito({
          detalhe: err.payload?.detalhe || err.message,
          interferencias: err.payload?.interferencias || [],
        });
      } else {
        setErro(err?.message || "Não foi possível salvar a alocação.");
      }
      throw err;
    }
  };

  const titulo = modo === "reatribuir" ? "Reatribuir etapa" : "Alocar etapa";

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={titulo}
      subtitle={
        cardSelecionado
          ? `${cardSelecionado.etapa_label} · ${cardSelecionado.pedido_numero} · ${cardSelecionado.ambiente.numero}`
          : "Etapas de F6 prontas para alocar"
      }
      footer={
        <RodapePainel>
          {modo === "reatribuir" && (
            <AsyncButton
              variant="secondary"
              silentError
              successMessage="Etapa devolvida para “Sem responsável”."
              onClick={async () => {
                await desalocarEtapa(request, card.etapa_id);
                onSucesso();
                onFechar();
              }}
            >
              Desalocar
            </AsyncButton>
          )}
          <SecondaryButton type="button" onClick={onFechar}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            silentError
            successMessage="Alocação salva."
            onClick={() => salvar(false)}
          >
            Salvar alocação
          </AsyncButton>
        </RodapePainel>
      }
    >
      {modo === "reatribuir" && contexto?.deColaborador && (
        <InfoMsg style={{ marginBottom: 14 }}>
          De: <strong>{contexto.deColaborador.nome}</strong>
          {" → "}
          Para: <strong>{operadorSelecionado?.nome || "—"}</strong>
        </InfoMsg>
      )}

      {card ? (
        <CampoForm as="div">
          <RotuloCampo>Etapa</RotuloCampo>
          <InfoMsg style={{ margin: 0 }}>
            {card.etapa_label} · {card.pedido_numero} · {card.ambiente.numero} —{" "}
            {card.ambiente.nome}
          </InfoMsg>
        </CampoForm>
      ) : (
        <CampoForm>
          <RotuloCampo>Etapa (sem responsável) *</RotuloCampo>
          <Select value={etapaId} onChange={(e) => setEtapaId(e.target.value)}>
            <option value="">Selecione…</option>
            {semResponsavel.map((c) => (
              <option key={c.etapa_id} value={c.etapa_id}>
                {c.etapa_label} · {c.ambiente.numero} · {c.pedido_numero}
                {c.cliente_nome ? ` · ${c.cliente_nome}` : ""}
              </option>
            ))}
          </Select>
          {semResponsavel.length === 0 && (
            <NotaDiscreta>
              Nenhuma etapa de F6 aguardando responsável neste momento.
            </NotaDiscreta>
          )}
        </CampoForm>
      )}

      <CampoForm>
        <RotuloCampo>Colaborador *</RotuloCampo>
        <Select
          value={operadorId}
          onChange={(e) => setOperadorId(e.target.value)}
        >
          <option value="">Selecione…</option>
          {colaboradores.map((c) => (
            <option
              key={c.id}
              value={c.id}
              disabled={c.indisponivel?.total}
            >
              {c.nome}
              {c.nivel ? ` · ${c.nivel}` : ""}
              {c.capacidade?.pct !== null && c.capacidade?.pct !== undefined
                ? ` · ${fmtNum(c.capacidade.pct, 0)}%${c.capacidade.pct > 100 ? " ⚠" : ""}`
                : ""}
              {c.indisponivel?.total ? " · indisponível" : ""}
            </option>
          ))}
        </Select>
        {sobrecarregado && (
          <WarningMsg style={{ margin: 0 }}>
            {operadorSelecionado.nome} já está em{" "}
            {fmtNum(operadorSelecionado.capacidade.pct, 0)}% no período —
            sobrecarga. A decisão é do gestor (não bloqueia).
          </WarningMsg>
        )}
        {semCompetencia && (
          <WarningMsg style={{ margin: 0 }}>
            {operadorSelecionado.nome} não tem competência registrada em{" "}
            {cardSelecionado.etapa_label} — pode alocar mesmo assim.
          </WarningMsg>
        )}
      </CampoForm>

      <GradeJanela>
        <CampoForm>
          <RotuloCampo>Data *</RotuloCampo>
          <CampoInput
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </CampoForm>
        <CampoForm>
          <RotuloCampo>Início *</RotuloCampo>
          <CampoInput
            type="time"
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
          />
        </CampoForm>
        <CampoForm>
          <RotuloCampo>Fim *</RotuloCampo>
          <CampoInput
            type="time"
            value={horaFim}
            onChange={(e) => setHoraFim(e.target.value)}
          />
        </CampoForm>
      </GradeJanela>

      {conflito && (
        <ErrorMsg style={{ marginBottom: 14 }}>
          {conflito.detalhe}
          <div style={{ marginTop: 8 }}>
            <AsyncButton
              variant="danger"
              silentError
              successMessage="Alocação salva (com interferência)."
              onClick={() => salvar(true)}
            >
              Alocar mesmo assim
            </AsyncButton>
          </div>
        </ErrorMsg>
      )}
      {erro && <ErrorMsg style={{ marginBottom: 14 }}>{erro}</ErrorMsg>}

      <CampoForm>
        <RotuloCampo>Observação</RotuloCampo>
        <AreaTexto
          rows={2}
          maxLength={300}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Contexto da alocação (opcional)"
        />
      </CampoForm>

      {cardSelecionado?.etapa_codigo === ETAPA_PINTURA && (
        <SecondaryButton
          type="button"
          onClick={() => navigate("/terceirizacao/oses/criar")}
          title="A Pintura é terceirizável — despacha ao lacador em vez de alocar"
        >
          Enviar ao lacador (OSE)
        </SecondaryButton>
      )}
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Slide-overs de disponibilidade (toggle Ativo/Inativo do período — D8)
// ---------------------------------------------------------------------------

function IndisponibilidadePanel({ aberto, colaborador, intervalo, onFechar, onSucesso }) {
  const { request } = useAuth();
  const [motivo, setMotivo] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setMotivo("");
    setInicio(intervalo.inicio);
    setFim(intervalo.fim);
    setObservacao("");
    setErro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Marcar indisponível"
      subtitle={colaborador ? colaborador.nome : undefined}
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            silentError
            disabled={!motivo || !inicio || !fim}
            successMessage="Indisponibilidade registrada."
            onClick={async () => {
              setErro(null);
              try {
                await criarIndisponibilidadeOperador(request, colaborador.id, {
                  inicio,
                  fim,
                  motivo,
                  observacao: observacao.trim(),
                });
                onSucesso();
                onFechar();
              } catch (err) {
                setErro(err?.message || "Não foi possível registrar.");
                throw err;
              }
            }}
          >
            Marcar indisponível
          </AsyncButton>
        </RodapePainel>
      }
    >
      <NotaDiscreta style={{ marginBottom: 14 }}>
        Disponibilidade TEMPORAL (férias/falta/afastamento/outra fábrica) —
        não é o soft-delete do cadastro. A coluna fica esmaecida, não recebe
        alocação e sai da capacidade dos KPIs no período.
      </NotaDiscreta>
      <CampoForm>
        <RotuloCampo>Motivo *</RotuloCampo>
        <Select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          <option value="">Selecione…</option>
          {MOTIVOS_INDISPONIBILIDADE.map((m) => (
            <option key={m.codigo} value={m.codigo}>
              {m.label}
            </option>
          ))}
        </Select>
      </CampoForm>
      <GradeJanela style={{ gridTemplateColumns: "1fr 1fr" }}>
        <CampoForm>
          <RotuloCampo>De *</RotuloCampo>
          <CampoInput
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
          />
        </CampoForm>
        <CampoForm>
          <RotuloCampo>Até *</RotuloCampo>
          <CampoInput
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
          />
        </CampoForm>
      </GradeJanela>
      {erro && <ErrorMsg style={{ marginBottom: 14 }}>{erro}</ErrorMsg>}
      <CampoForm>
        <RotuloCampo>Observação</RotuloCampo>
        <AreaTexto
          rows={2}
          maxLength={300}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Opcional"
        />
      </CampoForm>
    </SlideOverPanel>
  );
}

function ReativarPanel({ aberto, colaborador, intervalo, onFechar, onSucesso }) {
  const { request } = useAuth();
  const [janelas, setJanelas] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!aberto || !colaborador?.id) return;
    setJanelas(null);
    setErro(null);
    listarIndisponibilidades(request, colaborador.id)
      .then((resposta) => {
        const todas = resposta?.indisponibilidades || [];
        setJanelas(
          todas.filter(
            (j) => j.inicio <= intervalo.fim && j.fim >= intervalo.inicio,
          ),
        );
      })
      .catch((err) =>
        setErro(err?.message || "Não foi possível carregar as janelas."),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, colaborador?.id]);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Reativar no período"
      subtitle={colaborador ? colaborador.nome : undefined}
    >
      <NotaDiscreta style={{ marginBottom: 14 }}>
        Janelas de indisponibilidade que cruzam o período selecionado.
        Remover a janela devolve o colaborador à capacidade do board.
      </NotaDiscreta>
      {erro && <ErrorMsg style={{ marginBottom: 12 }}>{erro}</ErrorMsg>}
      {janelas === null && !erro && <EmptyMsg>Carregando…</EmptyMsg>}
      {janelas !== null && janelas.length === 0 && (
        <EmptyMsg>Nenhuma janela de indisponibilidade neste período.</EmptyMsg>
      )}
      {(janelas || []).map((j) => (
        <SugestaoBox key={j.id}>
          <Mono>
            {formatarDataCurta(j.inicio)} – {formatarDataCurta(j.fim)}
          </Mono>{" "}
          · {j.motivo_display || j.motivo}
          {j.observacao ? ` — ${j.observacao}` : ""}
          <div className="acoes">
            <AsyncButton
              variant="secondary"
              silentError
              successMessage="Janela removida — colaborador reativado."
              onClick={async () => {
                await removerIndisponibilidadeOperador(
                  request, colaborador.id, j.id,
                );
                setJanelas((atual) =>
                  (atual || []).filter((x) => x.id !== j.id),
                );
                onSucesso();
              }}
            >
              Remover janela
            </AsyncButton>
          </div>
        </SugestaoBox>
      ))}
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Slide-over Sugestões (Bloco D — placeholder rotulado, NUNCA "IA")
// ---------------------------------------------------------------------------

function SugestoesPanel({ aberto, sugestoes, onIgnorar, onFechar }) {
  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Sugestões — motor de regras a definir"
      subtitle="Exemplos ilustrativos gerados dos dados do período"
    >
      <div style={{ marginBottom: 14 }}>
        <Pill $tone="neutral">Placeholder</Pill>
      </div>
      {sugestoes.length === 0 && (
        <EmptyMsg>Nenhuma sugestão para o período atual.</EmptyMsg>
      )}
      {sugestoes.map((s) => (
        <SugestaoBox key={s.id}>
          {s.texto}
          <div className="acoes">
            <SecondaryButton type="button" onClick={s.aplicar}>
              Aplicar
            </SecondaryButton>
            <BotaoGhost type="button" onClick={() => onIgnorar(s.id)}>
              Ignorar
            </BotaoGhost>
          </div>
        </SugestaoBox>
      ))}
      <NotaDiscreta>
        Os critérios e pesos das sugestões ainda não foram configurados: por
        enquanto elas são exemplos e não consideram a carga real da fábrica.
      </NotaDiscreta>
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function Marcenaria() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();

  const podeVer = hasPermission("ppcp:view");
  const podeAlocar = hasPermission("marcenaria:alocar");
  const podeGerirOperador = hasPermission("operador:manage");

  const [periodo, setPeriodo] = useState(lerPeriodoInicial);
  const [board, setBoard] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [filtroEtapas, setFiltroEtapas] = useState(() => new Set());
  const [filtroAberto, setFiltroAberto] = useState(false);
  const filtroRef = useRef(null);

  const [painelAlocar, setPainelAlocar] = useState(null); // {modo, card?, ...}
  const [painelSugestoes, setPainelSugestoes] = useState(false);
  const [painelIndisp, setPainelIndisp] = useState(null); // colaborador
  const [painelReativar, setPainelReativar] = useState(null); // colaborador
  const [ignoradas, setIgnoradas] = useState(() => new Set());

  const [colunaDestacada, setColunaDestacada] = useState(null);
  const colunaRefs = useRef({});
  const semRespRef = useRef(null);
  const highlightTimer = useRef(null);

  const intervalo = useMemo(() => intervaloDoPeriodo(periodo), [periodo]);
  const rotulo = useMemo(() => rotuloPeriodo(periodo, intervalo), [periodo, intervalo]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_PERIODO, JSON.stringify(periodo));
    } catch { /* storage indisponível */ }
  }, [periodo]);

  useEffect(() => () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
  }, []);

  // Fecha o popover do filtro ao clicar fora / ESC.
  useEffect(() => {
    if (!filtroAberto) return undefined;
    const onClickFora = (e) => {
      if (filtroRef.current && !filtroRef.current.contains(e.target)) {
        setFiltroAberto(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setFiltroAberto(false);
    };
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onEsc);
    };
  }, [filtroAberto]);

  const carregar = useCallback(async () => {
    if (!podeVer) {
      setCarregando(false);
      return;
    }
    setErro(null);
    setCarregando(true);
    try {
      setBoard(
        await boardMarcenaria(request, {
          inicio: intervalo.inicio,
          fim: intervalo.fim,
        }),
      );
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar a alocação.");
    } finally {
      setCarregando(false);
    }
  }, [request, podeVer, intervalo.inicio, intervalo.fim]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const colaboradores = useMemo(
    () => board?.colaboradores || [],
    [board],
  );

  const passaFiltro = useCallback(
    (card) => filtroEtapas.size === 0 || filtroEtapas.has(card.etapa_codigo),
    [filtroEtapas],
  );

  const semResponsavel = useMemo(
    () => (board?.sem_responsavel || []).filter(passaFiltro),
    [board, passaFiltro],
  );
  const aguardandoOse = useMemo(
    () => (board?.aguardando_ose || []).filter(passaFiltro),
    [board, passaFiltro],
  );
  const cardsPorColaborador = useMemo(() => {
    const mapa = {};
    colaboradores.forEach((c) => {
      mapa[c.id] = (c.cards || []).filter(passaFiltro);
    });
    return mapa;
  }, [colaboradores, passaFiltro]);

  const kpis = useMemo(() => board?.kpis || {}, [board]);

  const scrollAteColuna = useCallback((id) => {
    const alvo = id === "sem-responsavel"
      ? semRespRef.current
      : colunaRefs.current[id];
    if (!alvo) return;
    alvo.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setColunaDestacada(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(
      () => setColunaDestacada(null),
      HIGHLIGHT_MS,
    );
  }, []);

  // ---- Sugestões ilustrativas (placeholder — dados reais, regras exemplo) --
  const sugestoes = useMemo(() => {
    if (!board) return [];
    const items = [];
    const ativos = colaboradores.filter((c) => !c.indisponivel?.total);
    const sobre = ativos
      .filter((c) => c.capacidade?.pct !== null && c.capacidade?.pct > 100)
      .sort((a, b) => b.capacidade.pct - a.capacidade.pct);
    const folga = ativos
      .filter((c) => c.capacidade?.pct !== null && c.capacidade?.pct < 85)
      .sort((a, b) => a.capacidade.pct - b.capacidade.pct);

    if (sobre.length > 0 && folga.length > 0) {
      const origem = sobre[0];
      const destino = folga[0];
      const duracao = (c) =>
        new Date(c.janela_fim).getTime() - new Date(c.janela_inicio).getTime();
      const menor = [...(origem.cards || [])]
        .filter((c) => c.janela_inicio && c.janela_fim)
        .sort((a, b) => duracao(a) - duracao(b))[0];
      if (menor) {
        items.push({
          id: `mover-${menor.etapa_id}`,
          texto: `${origem.nome} está em ${fmtNum(origem.capacidade.pct, 0)}%. `
            + `Mover "${menor.etapa_label} ${menor.ambiente.numero}" `
            + `(${horaCurta(menor.janela_inicio)}–${horaCurta(menor.janela_fim)}) `
            + `para ${destino.nome} (${fmtNum(destino.capacidade.pct, 0)}%)?`,
          aplicar: () => {
            setPainelSugestoes(false);
            setPainelAlocar({
              modo: "reatribuir",
              card: menor,
              deColaborador: origem,
              paraOperadorId: destino.id,
            });
          },
        });
      }
    }

    if (semResponsavel.length > 0 && folga.length > 0) {
      items.push({
        id: "distribuir-sem-responsavel",
        texto: `${semResponsavel.length} etapa(s) do período sem responsável. `
          + `Distribuir pelos ${folga.length} colaborador(es) com folga?`,
        aplicar: () => {
          setPainelSugestoes(false);
          setPainelAlocar({
            modo: "alocar",
            card: semResponsavel[0],
            paraOperadorId: folga[0].id,
          });
        },
      });
    }

    const pintura = [
      ...colaboradores.flatMap((c) => c.cards || []),
      ...semResponsavel,
    ].find((c) => c.etapa_codigo === ETAPA_PINTURA && !c.aguardando_ose);
    if (pintura) {
      items.push({
        id: `ose-${pintura.etapa_id}`,
        texto: `Pintura do ${pintura.pedido_numero} é terceirizável — enviar `
          + "ao lacador (OSE)?",
        aplicar: () => {
          setPainelSugestoes(false);
          navigate("/terceirizacao/oses/criar");
        },
      });
    }
    return items.filter((s) => !ignoradas.has(s.id));
  }, [board, colaboradores, semResponsavel, ignoradas, navigate]);

  // ---- KPIs ----
  const capacidadePct = kpis.capacidade_alocada_pct;
  const faixaCapacidade = faixaUtilizacao(capacidadePct);
  const itensKpi = useMemo(() => [
    {
      label: "Capacidade alocada",
      value: capacidadePct === null || capacidadePct === undefined ? (
        <span title="sem executor cadastrado">—</span>
      ) : (
        `${fmtNum(capacidadePct, 1)}%`
      ),
      sub: (
        <MiniBarra
          $pct={capacidadePct ?? 0}
          $faixa={capacidadePct == null ? "neutral" : faixaCapacidade}
          aria-hidden="true"
        >
          <span />
        </MiniBarra>
      ),
    },
    {
      label: "Sobrecarregados",
      value:
        (kpis.sobrecarregados?.total ?? 0) > 0 ? (
          <span style={{ color: "var(--danger-text)" }}>
            {kpis.sobrecarregados.total}
          </span>
        ) : (
          "–"
        ),
      sub: (kpis.sobrecarregados?.pcts || [])
        .map((p) => `${fmtNum(p, 0)}%`)
        .join(" · ") || "ninguém acima de 100%",
    },
    {
      label: "Etapas sem responsável",
      value: kpis.etapas_sem_responsavel ?? "–",
      sub: "clique para ver a fila",
      onClick: () => scrollAteColuna("sem-responsavel"),
    },
    {
      label: "SLA em risco",
      value:
        (kpis.sla_em_risco ?? 0) > 0 ? (
          <span style={{ color: "var(--danger-text)" }}>{kpis.sla_em_risco}</span>
        ) : (
          "–"
        ),
      sub: "buffer na zona vermelha",
    },
  ], [kpis, capacidadePct, faixaCapacidade, scrollAteColuna]);

  // ---- helpers de abertura de painéis ----
  const abrirAlocarLivre = () => setPainelAlocar({ modo: "criar" });
  const abrirAlocarCard = (card) => setPainelAlocar({ modo: "alocar", card });
  const abrirReatribuir = (card, colaborador) =>
    setPainelAlocar({ modo: "reatribuir", card, deColaborador: colaborador });

  const semExecutores = !carregando && !erro && colaboradores.length === 0;
  const modoSemana = periodo.modo === "semana";

  if (!podeVer) {
    return (
      <PageContainer>
        <EntityHeader
          title="Planejamento Marcenaria"
          subtitle="Alocação de etapas de F6 por executor de produção"
        />
        <EmptyState
          tone="locked"
          titulo="Sem acesso ao planejamento"
          descricao="Esta tela exige a permissão ppcp:view. Fale com o gestor de acessos."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Planejamento Marcenaria"
        subtitle="Alocação de etapas de F6 por executor de produção"
        badges={[{ label: rotulo, tone: "neutral" }]}
      />

      <BarraFerramentas style={{ justifyContent: "flex-start" }}>
        <ToolbarEsquerda>
          <GrupoModo role="tablist" aria-label="Período">
            <ChipModo
              type="button"
              role="tab"
              aria-selected={periodo.modo === "dia"}
              $ativo={periodo.modo === "dia"}
              onClick={() => setPeriodo((p) => ({ ...p, modo: "dia" }))}
            >
              Dia
            </ChipModo>
            <ChipModo
              type="button"
              role="tab"
              aria-selected={periodo.modo === "semana"}
              $ativo={periodo.modo === "semana"}
              onClick={() => setPeriodo((p) => ({ ...p, modo: "semana" }))}
            >
              Semana
            </ChipModo>
          </GrupoModo>
          <InputData
            type="date"
            value={periodo.ancora}
            onChange={(e) =>
              e.target.value
              && setPeriodo((p) => ({ ...p, ancora: e.target.value }))
            }
            aria-label="Data do período"
          />
          <RotuloPeriodoTag>{rotulo}</RotuloPeriodoTag>

          <Dropdown ref={filtroRef}>
            <button
              type="button"
              className="btn btn--ghost"
              aria-haspopup="menu"
              aria-expanded={filtroAberto}
              onClick={() => setFiltroAberto((v) => !v)}
            >
              Filtrar etapa
              {filtroEtapas.size > 0 && (
                <BadgeContagem>{filtroEtapas.size}</BadgeContagem>
              )}
            </button>
            {filtroAberto && (
              <DropdownLista role="menu">
                {ETAPAS_F6.map((etapa) => (
                  <OpcaoCheck key={etapa.codigo}>
                    <input
                      type="checkbox"
                      checked={filtroEtapas.has(etapa.codigo)}
                      onChange={() =>
                        setFiltroEtapas((atual) => {
                          const novo = new Set(atual);
                          if (novo.has(etapa.codigo)) novo.delete(etapa.codigo);
                          else novo.add(etapa.codigo);
                          return novo;
                        })
                      }
                    />
                    {etapa.label}
                  </OpcaoCheck>
                ))}
                {filtroEtapas.size > 0 && (
                  <BotaoGhost
                    type="button"
                    onClick={() => setFiltroEtapas(new Set())}
                  >
                    Limpar filtro
                  </BotaoGhost>
                )}
              </DropdownLista>
            )}
          </Dropdown>
        </ToolbarEsquerda>

        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setPainelSugestoes(true)}
        >
          ✨ Sugestões
          <BadgeContagem>{sugestoes.length}</BadgeContagem>
        </button>
        {podeAlocar && (
          <PrimaryButton type="button" onClick={abrirAlocarLivre}>
            + Alocar etapa
          </PrimaryButton>
        )}
      </BarraFerramentas>

      <KpiStrip items={itensKpi} />

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar a alocação. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar novamente
          </SecondaryButton>
        </LinhaErro>
      )}

      {carregando ? (
        <BoardScroll aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ flex: "0 0 292px" }}>
              <Shimmer $h={320} />
            </div>
          ))}
        </BoardScroll>
      ) : semExecutores ? (
        <EmptyState
          tone="info"
          titulo="Nenhum executor de produção ativo neste período"
          descricao="O board deriva do cadastro de colaboradores (contrato RH): aparece quem tem a flag executor de produção e está ativo no período."
          acoes={[
            {
              label: "Configurar colaboradores",
              onClick: () => navigate("/producao/config?dominio=mao_de_obra"),
            },
          ]}
        />
      ) : !erro && (
        <BoardScroll>
          {/* Pseudo-coluna "Sem responsável" — origem do KPI #3 */}
          <ColunaBoard
            ref={semRespRef}
            $destacada={colunaDestacada === "sem-responsavel"}
          >
            <HeaderColuna>
              <LinhaNome>
                <span className="nome">Sem responsável</span>
                <Pill $tone={semResponsavel.length > 0 ? "warning" : "neutral"}>
                  {semResponsavel.length}
                </Pill>
              </LinhaNome>
              <NotaDiscreta style={{ margin: 0 }}>
                Etapas de F6 prontas para alocar
              </NotaDiscreta>
            </HeaderColuna>
            {semResponsavel.length === 0 ? (
              <ColunaVazia>
                Nenhuma etapa de F6 pronta para alocar em {rotulo}.
              </ColunaVazia>
            ) : (
              <ListaCards>
                {semResponsavel.map((card) => (
                  <CardEtapa
                    key={card.etapa_id}
                    card={card}
                    modoSemana={modoSemana}
                    onCorpo={() =>
                      podeAlocar
                        ? abrirAlocarCard(card)
                        : navigate(`/producao/ambientes/${card.ambiente.id}`)
                    }
                  />
                ))}
              </ListaCards>
            )}
          </ColunaBoard>

          {/* Colunas por executor */}
          {colaboradores.map((col) => {
            const cards = cardsPorColaborador[col.id] || [];
            const cap = col.capacidade || {};
            const indisponivel = col.indisponivel?.total;
            const faixa = faixaUtilizacao(cap.pct);
            return (
              <ColunaBoard
                key={col.id}
                ref={(el) => {
                  colunaRefs.current[col.id] = el;
                }}
                $esmaecida={indisponivel}
                $destacada={colunaDestacada === col.id}
              >
                <HeaderColuna>
                  <LinhaNome>
                    <UserAvatar nome={col.nome} size="xs" />
                    <span className="nome" title={col.nome}>
                      {col.nome}
                    </span>
                    {indisponivel && (
                      <Pill $tone="neutral">
                        Inativo
                        {col.indisponivel?.motivo_display
                          ? ` · ${col.indisponivel.motivo_display}`
                          : ""}
                      </Pill>
                    )}
                  </LinhaNome>
                  <LinhaCargo>
                    <span>{col.funcao_display}</span>
                    {col.nivel && (
                      <Pill $tone={NIVEL_OPERADOR_TONES[col.nivel] || "neutral"}>
                        {col.nivel}
                      </Pill>
                    )}
                    {col.anos_casa !== null && col.anos_casa !== undefined && (
                      <span>
                        {col.anos_casa} {col.anos_casa === 1 ? "ano" : "anos"}
                      </span>
                    )}
                    {!indisponivel && (col.indisponivel?.dias || 0) > 0 && (
                      <Pill $tone="neutral" title="Dias indisponíveis no período">
                        {col.indisponivel.dias}d indisp.
                      </Pill>
                    )}
                  </LinhaCargo>
                  <LinhaCapacidade>
                    <MiniBarra $pct={cap.pct ?? 0} $faixa={faixa} aria-hidden="true">
                      <span />
                    </MiniBarra>
                    <span title="Horas alocadas / capacidade do período">
                      {cap.pct === null || cap.pct === undefined
                        ? "—"
                        : `${fmtNum(cap.pct, 0)}%`}
                      {" · "}
                      {fmtHoras(cap.horas_alocadas)} / {fmtHoras(cap.horas_capacidade)}
                    </span>
                  </LinhaCapacidade>
                  <LinhaCapacidade as="div" style={{ justifyContent: "space-between" }}>
                    <span>
                      {cards.length} etapa{cards.length === 1 ? "" : "s"}
                      {filtroEtapas.size > 0 ? " (filtro)" : ""}
                    </span>
                    {podeGerirOperador && (
                      <ToggleAtivo aria-label="Disponibilidade no período">
                        <button
                          type="button"
                          data-ativo={!indisponivel}
                          onClick={() =>
                            indisponivel && setPainelReativar(col)
                          }
                        >
                          Ativo
                        </button>
                        <button
                          type="button"
                          data-ativo={indisponivel}
                          onClick={() =>
                            !indisponivel && setPainelIndisp(col)
                          }
                        >
                          Inativo
                        </button>
                      </ToggleAtivo>
                    )}
                  </LinhaCapacidade>
                </HeaderColuna>
                {cards.length === 0 ? (
                  <ColunaVazia>
                    {indisponivel
                      ? "Indisponível no período — não recebe alocação."
                      : `Nenhuma etapa alocada em ${rotulo}.`}
                  </ColunaVazia>
                ) : (
                  <ListaCards>
                    {cards.map((card) => (
                      <CardEtapa
                        key={card.etapa_id}
                        card={card}
                        modoSemana={modoSemana}
                        onCorpo={() =>
                          navigate(`/producao/ambientes/${card.ambiente.id}`)
                        }
                        onReatribuir={
                          podeAlocar
                            ? () => abrirReatribuir(card, col)
                            : undefined
                        }
                      />
                    ))}
                  </ListaCards>
                )}
              </ColunaBoard>
            );
          })}

          {/* Trilho paralelo "Aguardando OSE" (D9) — fora das pessoas */}
          <TrilhoOse as="section">
            <HeaderColuna>
              <LinhaNome>
                <span className="nome">Aguardando OSE</span>
                <Pill $tone="neutral">{aguardandoOse.length}</Pill>
              </LinhaNome>
              <NotaDiscreta style={{ margin: 0 }}>
                Pintura despachada ao terceiro — buffer pausado
              </NotaDiscreta>
            </HeaderColuna>
            {aguardandoOse.length === 0 ? (
              <ColunaVazia>Nenhuma etapa fora na OSE</ColunaVazia>
            ) : (
              <ListaCards>
                {aguardandoOse.map((card) => (
                  <CardOse
                    key={card.etapa_id}
                    onClick={() =>
                      navigate(`/producao/ambientes/${card.ambiente.id}`)
                    }
                    title={`${card.ambiente.numero} · ${card.ambiente.nome}`}
                  >
                    <TituloCard>
                      {card.ambiente.nome} — {card.etapa_label}
                    </TituloCard>
                    <LinhaPills>
                      <Pill $tone="warning">Buffer pausado</Pill>
                      {card.ose?.numero && (
                        <Pill $tone="neutral">{card.ose.numero}</Pill>
                      )}
                    </LinhaPills>
                    <div className="linha">
                      <span>{card.ose?.parceiro || "Parceiro"}</span>
                    </div>
                    <div className="linha">
                      <span>Saída</span>
                      <Mono>{formatarDataHoraCurta(card.ose?.enviada_em)}</Mono>
                    </div>
                    <div className="linha">
                      <span>Retorno prev.</span>
                      <Mono>
                        {formatarDataHoraCurta(card.ose?.prazo_retorno)}
                      </Mono>
                    </div>
                  </CardOse>
                ))}
              </ListaCards>
            )}
          </TrilhoOse>
        </BoardScroll>
      )}

      {/* Bloco B — Capacidade / Interferências (raio-x) */}
      {!carregando && !semExecutores && !erro && (
        <section>
          <SectionTitle>Capacidade e interferências</SectionTitle>
          <TabelaFixa>
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Cargo / Nível</th>
                <th>Etapas</th>
                <th>Horas aloc. / cap.</th>
                <th>Utilização</th>
                <th>Conflitos</th>
                <th>SLA risco</th>
              </tr>
            </thead>
            <tbody>
              {[...colaboradores]
                .sort(
                  (a, b) =>
                    Number(Boolean(a.indisponivel?.total))
                    - Number(Boolean(b.indisponivel?.total)),
                )
                .map((col) => {
                  const cards = col.cards || [];
                  const conflitos = cards.filter((c) => c.interferencia).length;
                  const slaRisco = cards.filter((c) => c.sla_risco).length;
                  const cap = col.capacidade || {};
                  const faixa = faixaUtilizacao(cap.pct);
                  const indisponivel = col.indisponivel?.total;
                  return (
                    <LinhaTabelaEsmaecida
                      key={col.id}
                      data-clicavel="true"
                      $esmaecida={indisponivel}
                      onClick={() => scrollAteColuna(col.id)}
                      title="Focar a coluna no board"
                    >
                      <td title={col.nome}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <UserAvatar nome={col.nome} size="xs" />
                          {col.nome}
                        </span>
                      </td>
                      <td>
                        {col.funcao_display}
                        {col.nivel ? (
                          <>
                            {" · "}
                            <Pill
                              $tone={NIVEL_OPERADOR_TONES[col.nivel] || "neutral"}
                            >
                              {col.nivel}
                            </Pill>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <Mono>{cap.etapas_count ?? cards.length}</Mono>
                      </td>
                      <td>
                        <Mono>
                          {fmtHoras(cap.horas_alocadas)} /{" "}
                          {fmtHoras(cap.horas_capacidade)}
                        </Mono>
                      </td>
                      <td>
                        {cap.pct === null || cap.pct === undefined ? (
                          "—"
                        ) : (
                          <Pill
                            $tone={
                              faixa === "success"
                                ? "success"
                                : faixa === "warning"
                                  ? "warning"
                                  : "danger"
                            }
                          >
                            {fmtNum(cap.pct, 0)}%
                          </Pill>
                        )}
                      </td>
                      <td>
                        {conflitos > 0 ? (
                          <Pill $tone="danger">{conflitos}</Pill>
                        ) : (
                          "–"
                        )}
                      </td>
                      <td>
                        {slaRisco > 0 ? (
                          <Pill $tone="danger">{slaRisco}</Pill>
                        ) : (
                          "–"
                        )}
                      </td>
                    </LinhaTabelaEsmaecida>
                  );
                })}
            </tbody>
          </TabelaFixa>
          <NotaDiscreta>
            Linha foca a coluna correspondente do board (não navega).
            Capacidade = calendário × eficiência da Config, descontando
            indisponibilidades do período.
          </NotaDiscreta>
        </section>
      )}

      <AlocarPanel
        aberto={Boolean(painelAlocar)}
        contexto={painelAlocar}
        colaboradores={colaboradores}
        semResponsavel={semResponsavel}
        intervalo={intervalo}
        onFechar={() => setPainelAlocar(null)}
        onSucesso={carregar}
      />

      <SugestoesPanel
        aberto={painelSugestoes}
        sugestoes={sugestoes}
        onIgnorar={(id) =>
          setIgnoradas((atual) => new Set(atual).add(id))
        }
        onFechar={() => setPainelSugestoes(false)}
      />

      <IndisponibilidadePanel
        aberto={Boolean(painelIndisp)}
        colaborador={painelIndisp}
        intervalo={intervalo}
        onFechar={() => setPainelIndisp(null)}
        onSucesso={carregar}
      />

      <ReativarPanel
        aberto={Boolean(painelReativar)}
        colaborador={painelReativar}
        intervalo={intervalo}
        onFechar={() => setPainelReativar(null)}
        onSucesso={carregar}
      />
    </PageContainer>
  );
}
